import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { Button } from "@/components/ui/button";
import { usePageMeta } from "@/hooks/use-page-meta";
import { getBaseUrl } from "@/lib/site";

export const ONBOARDING_SEEN_KEY = "buukd:onboarding-seen";

const SLIDES = [
  "/onboarding/Buukd_AppStore_Slide2.png",
  "/onboarding/Buukd_AppStore_Slide3.png",
  "/onboarding/Buukd_AppStore_Slide6.png",
  "/onboarding/Buukd_AppStore_Slide7.png",
  "/onboarding/Buukd_AppStore_Slide9.png",
  "/onboarding/Buukd_AppStore_Slide5.png",
];

const AUTOPLAY_INTERVAL_MS = 4000;

export default function Onboarding() {
  const navigate = useNavigate();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const autoplayTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const userInteracted = useRef(false);

  usePageMeta({
    title: "Welcome to Buukd",
    description: "Bookings, payments, and customer management in one app for your business.",
    path: "/onboarding",
  });

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    const onPointerDown = () => {
      userInteracted.current = true;
      if (autoplayTimer.current) {
        clearInterval(autoplayTimer.current);
        autoplayTimer.current = null;
      }
    };

    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("pointerDown", onPointerDown);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("pointerDown", onPointerDown);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const tick = () => {
      if (!emblaApi || userInteracted.current) return;
      emblaApi.scrollNext();
    };

    autoplayTimer.current = setInterval(tick, AUTOPLAY_INTERVAL_MS);
    return () => {
      if (autoplayTimer.current) {
        clearInterval(autoplayTimer.current);
        autoplayTimer.current = null;
      }
    };
  }, [emblaApi]);

  const markSeen = useCallback(() => {
    try {
      localStorage.setItem(ONBOARDING_SEEN_KEY, "1");
    } catch {
      // ignore storage errors
    }
  }, []);

  const handleSignIn = () => {
    markSeen();
    navigate("/signin");
  };

  const handleGetStarted = () => {
    markSeen();
    const url = `${getBaseUrl()}/signup`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="relative h-[100dvh] overflow-hidden bg-gradient-to-b from-[#6f5bd1] via-[#a868c9] to-[#e07ab0] font-bricolage">
      {/* Step indicators */}
      <div
        className="absolute top-0 left-0 right-0 z-20 flex justify-center gap-1.5 px-4"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.25rem)" }}
      >
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => {
              userInteracted.current = true;
              if (autoplayTimer.current) {
                clearInterval(autoplayTimer.current);
                autoplayTimer.current = null;
              }
              emblaApi?.scrollTo(i);
            }}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === selectedIndex ? "w-8 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>

      {/* Carousel */}
      <div className="absolute inset-0" ref={emblaRef}>
        <div className="flex h-full">
          {SLIDES.map((src, i) => (
            <div key={src} className="flex-[0_0_100%] min-w-0 h-full">
              <img
                src={src}
                alt={`Buukd feature ${i + 1}`}
                className="w-full h-full object-cover select-none"
                draggable={false}
                loading={i === 0 ? "eager" : "lazy"}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Bottom fade overlay for button legibility */}
      <div
        className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 h-48 bg-gradient-to-t from-black/50 via-black/20 to-transparent"
        aria-hidden="true"
      />

      {/* Floating CTAs */}
      <div
        className="absolute bottom-0 left-0 right-0 z-20 px-6 pt-6 space-y-3"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.75rem)" }}
      >
        <Button
          type="button"
          onClick={handleGetStarted}
          variant="gradient"
          className="w-full h-12 text-base shadow-lg"
        >
          Get started for free
        </Button>
        <Button
          type="button"
          onClick={handleSignIn}
          variant="outline"
          className="w-full h-12 text-base bg-white/95 backdrop-blur-sm border-white hover:bg-white"
        >
          <span className="bg-gradient-to-br from-pink-400 to-pink-600 bg-clip-text text-transparent font-semibold animate-gradient-subtle">
            Sign in
          </span>
        </Button>
      </div>
    </div>
  );
}
